import { ConnectionDetails, QueryInputType, convertConnectionToDetails, mergeConnectionDetails as merge, queryInput } from '@/utils/custom';
import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import {
  LostAndFoundIssueWithCache,
  LostAndFoundIssueWithCacheSearch,
  SearchLostAndFoundIssueWithCacheResponseLostAndFoundIssueWithCacheConnectionArgs,
  SortDirectionEnum,
} from '@/models/gen/graphql';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';
import { refetchLostAndFoundIssueDocument, searchLostAndFoundIssueDocument } from './searchLostAndFoundIssueDocument';

import { LostAndFoundIssueWithDocuments } from '../../components/LostAndFoundHistoryForm/types';
import { SearchLostAndFoundIssue } from '@/features/LostAndFoundHistory/api/queries/SearchLostAndFoundIssue';
import { TypedDocumentNode } from '@apollo/client';
import { getProperty } from '@/utils';
import graphApi from '@/api';
import { recreateLostAndFoundIssueDocumentDownloadUrl } from '@/features/LostAndFoundHistory/api/services/createLostAndFoundIssueDocumentDownloadURL';

// Service Function Signature
type ServiceMethodPayload = ({
  startDatetime?: string;
  endDatetime?: string;
  airportCode?: string[];
  tripServicerIataAirlineCodes?: string[];
  returnStatuses?: string[];
} & {
  [key in keyof LostAndFoundIssueWithCacheSearch]?: any;
})[];
interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<LostAndFoundIssueWithCache>>;
}
interface ServiceMethodWithDocuments {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<LostAndFoundIssueWithDocuments>>;
}

// SearchLostAndFoundIssue Service
export const { searchLostAndFoundIssue, refetchLostAndFoundIssue, useLostAndFoundIssue } = ((): {
  searchLostAndFoundIssue: ServiceMethod;
  refetchLostAndFoundIssue: ServiceMethod;
  useLostAndFoundIssue: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch, refetch] = graphApi(SearchLostAndFoundIssue).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<LostAndFoundIssueWithCache>> => {
        const query: LostAndFoundIssueWithCacheSearch[] = payload?.map((item) => ({
          tripId: queryInput.orNull(item?.id),
          createdAt:
            item?.startDatetime && item?.endDatetime
              ? queryInput.date([item?.startDatetime, item.endDatetime], false, SortDirectionEnum.Desc)
              : item?.id // If we are searching by trip id this is a history table, and we can get all the dispatch issues and sort by datetime
                ? queryInput([], QueryInputType.ISNOTNULL, SortDirectionEnum.Desc)
                : null,
          tripAirportCode: queryInput.orNull(item?.airportCode),
          tripServicerIataAirlineCode: queryInput.orNull(item?.tripServicerIataAirlineCodes),
          returnStatus: queryInput.orNull(item?.returnStatuses),
        }));
        const args: SearchLostAndFoundIssueWithCacheResponseLostAndFoundIssueWithCacheConnectionArgs = {
          input: {
            first: options?.pageSize || null,
            after: options?.pageSize > 0 ? (options?.pageSize * options?.page).toString() : null,
            query,
          },
        };
        const opts = { merge: options?.merge ? 'searchLostAndFoundIssue.lostAndFoundIssueTableConnection' : undefined };
        const res = await fn(args, opts);
        return convertConnectionToDetails(getProperty('searchLostAndFoundIssue.lostAndFoundIssueTableConnection', res));
      }
  );
  const useService = createGraphApiHook(fetch, { refetch, merge });
  return {
    searchLostAndFoundIssue: fetch,
    refetchLostAndFoundIssue: refetch,
    useLostAndFoundIssue: useService,
  };
})();

export const { searchLostAndFoundIssueWithDocuments, refetchLostAndFoundIssueWithDocuments } = ((): {
  searchLostAndFoundIssueWithDocuments: ServiceMethodWithDocuments;
  refetchLostAndFoundIssueWithDocuments: ServiceMethodWithDocuments;
} => {
  const fetch = async (
    payload?: ServiceMethodPayload,
    options?: ConnectionOptions
  ): Promise<ConnectionDetails<LostAndFoundIssueWithDocuments>> => {
    const lostAndFoundIssues = await searchLostAndFoundIssue(payload, options);
    const res: ConnectionDetails<LostAndFoundIssueWithDocuments> = {
      ...lostAndFoundIssues,
      rows: lostAndFoundIssues.rows.map((row) => ({
        ...row,
        documents: [],
      })),
    };
    const allIds = res.rows.map((row) => row.id);
    if (allIds?.length > 0) {
      const documents = await searchLostAndFoundIssueDocument([{ issueId: queryInput(allIds) }]);
      try {
        const downloadURLS = await recreateLostAndFoundIssueDocumentDownloadUrl({ issueId: allIds });
        documents.rows.forEach((document) => {
          const issueIndex = res.rows.findIndex((row) => row.id === document.issueId);
          const downloadUrlIndex = downloadURLS.urls.findIndex((url) => url.documentId === document.id);
          if (issueIndex !== -1) {
            res.rows[issueIndex].documents.push({ ...document, fileLocation: downloadURLS.urls[downloadUrlIndex]?.url });
          }
        });
      } catch (err) {
        console.error('Error creating download URLs:', err);
      }
    }
    return { ...res };
  };

  const refetch = async (
    payload?: ServiceMethodPayload,
    options?: ConnectionOptions
  ): Promise<ConnectionDetails<LostAndFoundIssueWithDocuments>> => {
    const LostAndFoundIssues = await refetchLostAndFoundIssue(payload, options);
    const res: ConnectionDetails<LostAndFoundIssueWithDocuments> = {
      ...LostAndFoundIssues,
      rows: LostAndFoundIssues.rows.map((row) => ({
        ...row,
        documents: [],
      })),
    };
    const allIds = res.rows.map((row) => row.id);
    if (allIds?.length > 0) {
      const documents = await refetchLostAndFoundIssueDocument([{ issueId: queryInput(allIds) }]);
      try {
        const downloadURLS = await recreateLostAndFoundIssueDocumentDownloadUrl({ issueId: allIds });
        documents.rows.forEach((document) => {
          const issueIndex = res.rows.findIndex((row) => row.id === document.issueId);
          const downloadUrlIndex = downloadURLS.urls.findIndex((url) => url.documentId === document.id);
          if (issueIndex !== -1) {
            res.rows[issueIndex].documents.push({ ...document, fileLocation: downloadURLS.urls[downloadUrlIndex]?.url });
          }
        });
      } catch (err) {
        console.error('Error creating download URLs:', err);
      }
    }
    return { ...res };
  };
  return {
    searchLostAndFoundIssueWithDocuments: fetch,
    refetchLostAndFoundIssueWithDocuments: refetch,
  };
})();
